import { useState, useEffect } from 'react';

import { CustomSizeModal } from './CustomSizeModal';
import { getAllStates, SavedState } from '../../../services/indexedDB';
import { Play, Plus } from 'lucide-react';
import TemplateEditorPage from '../../template-editor/TemplateEditorPage';




type TextProps = {
  text: string;
};

function Text({ text }: TextProps) {
  return (
    <div className="bg-[rgba(255,255,255,0.5)] content-stretch flex items-center justify-center px-[12px] py-[11px] relative rounded-[4px] shrink-0">
      <p className="font-sans font-normal leading-[normal] relative shrink-0 text-[#101928] text-[13px] text-nowrap" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
        {text}
      </p>
    </div>
  );
}

export const Hero = () => {

  const [isCustomSizeModalOpen, setIsCustomSizeModalOpen] = useState(false);
  const [lastState, setLastState] = useState<SavedState | null>(null);
  const [showEditor, setShowEditor] = useState(false);

  useEffect(() => {
    const fetchLast = async () => {
      const states = await getAllStates();
      if (states.length > 0) {
        const sorted = states.sort((a, b) => b.lastModified - a.lastModified);
        setLastState(sorted[0]);
      }
    };
    fetchLast();
  }, []);

  const handleCreateNew = () => {
    setIsCustomSizeModalOpen(true);
  };

  return (
    <div className="h-[256px] overflow-hidden relative rounded-[8px] shrink-0 w-full bg-[#FAFAFA]">
      <div className="absolute h-[385px] left-[-42px] top-[-271px] w-[513px]">
        <div className="absolute inset-[-129.87%_-97.47%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 1513 1385">
            <g filter="url(#filter0_f_58_750)" id="Ellipse 20">
              <ellipse cx="756.5" cy="692.5" fill="var(--fill-0, #F9D495)" rx="256.5" ry="192.5" />
            </g>
            <defs>
              <filter colorInterpolationFilters="sRGB" filterUnits="userSpaceOnUse" height="1385" id="filter0_f_58_750" width="1513" x="0" y="0">
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feBlend in="SourceGraphic" in2="BackgroundImageFix" mode="normal" result="shape" />
                <feGaussianBlur result="effect1_foregroundBlur_58_750" stdDeviation="250" />
              </filter>
            </defs>
          </svg>
        </div>
      </div>
      <div className="absolute h-[369px] right-0 top-[-224px] w-[573px]">
        <div className="absolute inset-[-135.5%_-87.26%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 1573 1369">
            <g filter="url(#filter0_f_58_735)" id="Ellipse 21">
              <ellipse cx="786.5" cy="684.5" fill="var(--fill-0, #BEA4F3)" rx="286.5" ry="184.5" />
            </g>
            <defs>
              <filter colorInterpolationFilters="sRGB" filterUnits="userSpaceOnUse" height="1369" id="filter0_f_58_735" width="1573" x="0" y="0">
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feBlend in="SourceGraphic" in2="BackgroundImageFix" mode="normal" result="shape" />
                <feGaussianBlur result="effect1_foregroundBlur_58_735" stdDeviation="250" />
              </filter>
            </defs>
          </svg>
        </div>
      </div>

      <div className="absolute content-stretch flex flex-col gap-[16px] items-start left-0 top-1/2 -translate-y-1/2 w-full px-[24px]">
        <div className="content-stretch flex flex-col gap-[20px] items-start relative shrink-0 w-full">
          <div className="content-stretch flex gap-[20px] items-center relative shrink-0 w-full">
            <div className="basis-0 content-stretch flex flex-col gap-[8px] grow items-start min-h-px min-w-px relative shrink-0">
              <p className="font-sans font-bold leading-[normal] relative shrink-0 text-[#1d2739] text-[28px] w-full" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                Design anything. Instantly.
              </p>
              <div className="flex items-center gap-3">
                <p className="font-sans font-normal leading-[24px] relative shrink-0 text-[#475367] text-[14px] tracking-[0.14px]" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                  Search ideas, remix templates, or start a black canvas in one click.
                </p>
                {lastState && (
                  <button
                    onClick={() => setShowEditor(true)}
                    className="flex items-center gap-2 bg-[#30A84B] hover:bg-[#288a3e] text-white px-3 py-1.5 rounded-full text-[12px] font-bold transition-all shadow-sm active:scale-95"
                  >
                    <Play size={12} fill="currentColor" />
                    Resume Last: {lastState.pageName || 'Untitled'}
                  </button>
                )}
              </div>
            </div>
            <div
              className="group bg-[#101928] content-stretch flex gap-[8px] h-[36px] items-center justify-center p-[12px] relative rounded-[6px] shrink-0 cursor-pointer hover:opacity-90 transition-opacity"
              data-name="Create New"
              onClick={handleCreateNew}
            >
              <Plus size={18} color="white" />
              <p className="font-sans font-semibold leading-[normal] relative shrink-0 text-[14px] text-nowrap text-white" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                Create New
              </p>
            </div>
            {/* Hidden as per user request
            <div
              className="group bg-[#101928] ...
            >
              ...
            </div>
            */}
          </div>
          <div className="content-stretch flex gap-[16px] items-center relative shrink-0 w-full">
            <Text text="Instagram post" />
            <Text text="YouTube thumbnail" />
            <Text text="Flyer" />
            <Text text="Presentation" />
            <Text text="Logo" />
          </div>
        </div>
      </div>

      <CustomSizeModal isOpen={isCustomSizeModalOpen} onClose={() => setIsCustomSizeModalOpen(false)} />

      {showEditor && lastState && (
        <div className="fixed inset-0 z-[9999] bg-white overflow-auto">
          <TemplateEditorPage
            initialTemplate={{
              id: lastState.id,
              name: lastState.pageName || 'Saved Design',
              previewUrl: ''
            }}
            onBack={() => setShowEditor(false)}
          />
        </div>
      )}
    </div>
  );
};

